/**
 * Grand Quack Auto — mobile touch controls.
 *
 * Virtual joystick (bottom-left) + quack button (bottom-right) + enter/exit
 * button. Only visible on touch-capable devices.
 *
 * Writes to module-level state that inputSystem reads each tick.
 */

// ── Shared state read by inputSystem ──────────────────────────────────────

export let touchMove = { x: 0, y: 0 };
export let touchQuack = false;
export let touchEnter = false;

let initialized = false;

/** Initialize touch controls. Call once from main.ts. */
export function initTouchControls(): void {
  if (initialized || !('ontouchstart' in window)) return;
  initialized = true;

  const hud = document.getElementById('hud');
  if (!hud) return;

  // ── Virtual joystick ────────────────────────────────────────────────
  const joyBase = document.createElement('div');
  joyBase.id = 'touch-joy-base';
  const joyKnob = document.createElement('div');
  joyKnob.id = 'touch-joy-knob';
  joyBase.appendChild(joyKnob);
  hud.appendChild(joyBase);

  let joyActive = false;
  let joyOrigin = { x: 0, y: 0 };
  let maxDragDist = 0; // track max drag distance for tap-to-stop detection
  const JOY_RADIUS = 40;
  const TAP_THRESHOLD = 8; // px — below this on touchend = "stop" tap

  joyBase.addEventListener('touchstart', (e) => {
    e.preventDefault();
    joyActive = true;
    maxDragDist = 0;
    const t = e.touches[0]!;
    const rect = joyBase.getBoundingClientRect();
    joyOrigin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    updateJoy(t.clientX, t.clientY);
  });

  joyBase.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (!joyActive) return;
    const t = e.touches[0]!;
    maxDragDist = Math.max(maxDragDist, Math.hypot(t.clientX - joyOrigin.x, t.clientY - joyOrigin.y));
    updateJoy(t.clientX, t.clientY);
  });

  joyBase.addEventListener('touchend', () => {
    joyActive = false;
    // Tap with no significant drag = stop. Otherwise keep last direction (latching).
    if (maxDragDist < TAP_THRESHOLD) {
      touchMove = { x: 0, y: 0 };
      joyKnob.style.transform = 'translate(-50%,-50%)';
    }
    // else: keep touchMove and knob at last position — duck keeps moving
  });

  function updateJoy(cx: number, cy: number): void {
    let dx = cx - joyOrigin.x;
    let dy = -(cy - joyOrigin.y); // invert Y — up = forward
    const dist = Math.hypot(dx, dy);
    if (dist > JOY_RADIUS) { dx = (dx / dist) * JOY_RADIUS; dy = (dy / dist) * JOY_RADIUS; }
    touchMove = { x: dx / JOY_RADIUS, y: dy / JOY_RADIUS };
    joyKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${-dy}px))`;
  }

  // ── Quack button ────────────────────────────────────────────────────
  const quackBtn = document.createElement('div');
  quackBtn.id = 'touch-quack';
  quackBtn.textContent = 'QUACK';
  hud.appendChild(quackBtn);

  quackBtn.addEventListener('touchstart', (e) => { e.preventDefault(); touchQuack = true; });
  quackBtn.addEventListener('touchend', () => { touchQuack = false; });

  // ── Enter/Exit button ───────────────────────────────────────────────
  const enterBtn = document.createElement('div');
  enterBtn.id = 'touch-enter';
  enterBtn.textContent = 'E';
  hud.appendChild(enterBtn);

  enterBtn.addEventListener('touchstart', (e) => { e.preventDefault(); touchEnter = true; });
  enterBtn.addEventListener('touchend', () => { touchEnter = false; });
}
